import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Crown, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { Plan, PlanName } from '../../model/professional/subscription-model';

interface SubscriptionPlanCardProps {
  plan: Plan;
  isCurrentPlan: boolean;
  isPopular?: boolean;
  onSelect: (planId: PlanName) => void;
  disabled?: boolean;
  buttonText?: string;
}

const SubscriptionPlanCard: React.FC<SubscriptionPlanCardProps> = ({
  plan,
  isCurrentPlan,
  isPopular = false,
  onSelect,
  disabled = false,
  buttonText
}) => {
  const { t } = useTranslation();

  // Détermine le thème selon le plan
  const getTheme = () => {
    if (isCurrentPlan) {
      return {
        badge: 'bg-primary/10 text-primary dark:bg-primary-dark/20 dark:text-primary-light',
        checkBg: 'bg-primary/10 dark:bg-primary-dark/20',
        checkText: 'text-primary dark:text-primary-light',
        border: 'border-2 border-primary dark:border-primary-light shadow-xl'
      };
    }

    switch (plan.id) {
      case 'junior':
        return {
          badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
          checkBg: 'bg-blue-100 dark:bg-blue-900/30',
          checkText: 'text-blue-800 dark:text-blue-300',
          border: 'border border-gray-200 dark:border-gray-700'
        };
      case 'premium':
        return {
          badge: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
          checkBg: 'bg-purple-100 dark:bg-purple-900/30',
          checkText: 'text-purple-800 dark:text-purple-300',
          border: 'border border-gray-200 dark:border-gray-700'
        };
      case 'premium_plus':
        return {
          badge: 'bg-gradient-to-r from-yellow-100 to-orange-100 text-orange-800 dark:from-yellow-900/30 dark:to-orange-900/30 dark:text-orange-300',
          checkBg: 'bg-gradient-to-r from-yellow-100 to-orange-100 dark:from-yellow-900/30 dark:to-orange-900/30',
          checkText: 'text-orange-800 dark:text-orange-300',
          border: 'border border-gray-200 dark:border-gray-700'
        };
      default:
        return {
          badge: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
          checkBg: 'bg-gray-100 dark:bg-gray-700',
          checkText: 'text-gray-800 dark:text-gray-300',
          border: 'border border-gray-200 dark:border-gray-700'
        };
    }
  };

  const theme = getTheme();

  // Formater les features pour l'affichage
  const getFeaturesList = () => {
    const features = [];

    // Limites
    features.push(
      t('subscription.features.companies', { count: plan.features.maxCompanies })
    );
    features.push(
      t('subscription.features.collaborators', { count: plan.features.maxCollaborators })
    );

    if (plan.features.maxActivePromoCodes === -1 || plan.features.maxActivePromoCodes === 999999) {
      features.push(t('subscription.features.promoCodesUnlimited'));
    } else if (plan.features.maxActivePromoCodes > 0) {
      features.push(
        t('subscription.features.promoCodes', { count: plan.features.maxActivePromoCodes })
      );
    }

    // Fonctionnalités booléennes
    if (plan.features.onlinePayment) {
      features.push(t('subscription.features.onlinePayment'));
    }
    if (plan.features.depositPayment) {
      features.push(t('subscription.features.depositPayment'));
    }
    if (plan.features.reviewSystem) {
      features.push(t('subscription.features.reviewSystem'));
    }
    if (plan.features.advancedAnalytics) {
      features.push(t('subscription.features.advancedAnalytics'));
    }
    if (plan.features.prioritySupport) {
      features.push(t('subscription.features.prioritySupport'));
    }

    return features;
  };

  const features = getFeaturesList();

  // Icône selon le plan
  const getPlanIcon = () => {
    switch (plan.id) {
      case 'junior':
        return <Sparkles className="w-5 h-5" />;
      case 'premium':
        return <Crown className="w-5 h-5" />;
      case 'premium_plus':
        return <Crown className="w-5 h-5" />;
      default:
        return null;
    }
  };

  // Gestion du clic
  const handleClick = () => {
    if (!disabled && !isCurrentPlan) {
      onSelect(plan.id);
    }
  };

  // Texte du bouton
  const getButtonText = () => {
    if (buttonText) return buttonText;
    if (isCurrentPlan) return t('subscription.currentPlan');
    return t('subscription.selectPlan');
  };

  return (
    <div
      className={`relative bg-white dark:bg-gray-800 p-6 rounded-2xl ${theme.border} hover:shadow-lg transition-all duration-300 flex flex-col h-full ${
        isPopular ? 'md:-translate-y-4 hover:-translate-y-5 z-10' : 'hover:-translate-y-1'
      }`}
    >
      {/* Badge "Populaire" */}
      {isPopular && (
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-1.5 rounded-full text-sm font-medium shadow-lg">
          {t('subscription.popular')}
        </div>
      )}

      {/* Badge "Plan actuel" */}
      {isCurrentPlan && (
        <div className="absolute top-4 right-4 bg-primary dark:bg-primary-dark text-white px-3 py-1 rounded-full text-xs font-medium">
          {t('subscription.current')}
        </div>
      )}

      {/* En-tête du plan */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <span className={`inline-flex items-center gap-1.5 ${theme.badge} px-3 py-1.5 rounded-full text-sm font-medium`}>
            {getPlanIcon()}
            {plan.name}
          </span>
        </div>

        {/* Prix */}
        <div className="mt-4">
          <div className="flex items-baseline">
            <span className="text-4xl font-bold text-gray-900 dark:text-white">
              {plan.price.toFixed(2)}€
            </span>
            <span className="text-gray-500 dark:text-gray-400 ml-2">
              {plan.period}
            </span>
          </div>
        </div>

        <p className="text-gray-600 dark:text-gray-400 mt-3 text-sm">
          {plan.description}
        </p>
      </div>

      {/* Liste des fonctionnalités */}
      <ul className="space-y-3 mb-6 flex-grow">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start gap-2">
            <div className={`${theme.checkBg} p-1 rounded-full mt-0.5 flex-shrink-0`}>
              <Check className={`w-3 h-3 ${theme.checkText}`} />
            </div>
            <span className="text-sm text-gray-700 dark:text-gray-300">{feature}</span>
          </li>
        ))}
      </ul>

      {/* Bouton d'action */}
      <Button
        onClick={handleClick}
        disabled={disabled || isCurrentPlan}
        variant={isCurrentPlan ? 'outline' : isPopular ? 'primary' : 'outline'}
        className="w-full"
      >
        {getButtonText()}
      </Button>
    </div>
  );
};

export default SubscriptionPlanCard;
